// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// "Read machine aloud" (G2-09; 06 §6, NFR-025, NFR-022).
//
// > *"A 'read machine aloud' control speaks the current ball, score, active mode, timer, what is lit
// > and what the next objective is — the direct analogue of Chess VR's board narration, free on
// > every tier."*
//
// **Free on every tier**, which is why nothing in this file asks about an entitlement. It is an
// accessibility commitment, not a feature: a player who cannot read a dot-matrix display at arm's
// length is entitled to know what the machine is doing, and a paywall in front of that is a paywall
// in front of playing at all.
//
// WHY THIS IS NOT `narration.ts`
// ------------------------------
// `narration.ts` answers NFR-025 — a live region that says what *changed*, so a screen reader
// announces a drain or a mode start as it happens. It is a diff, it fires unasked, and it is
// deliberately terse because it interrupts. This is the opposite: a **standing report**, asked for
// by pressing something, that describes the machine as it is right now and says the four things a
// sighted player reads off the playfield rather than off the display.
//
// Two of the six things 06 §6 names had no reader anywhere: **what is lit** and **the next
// objective**. The other four are already on the readout.
//
// WHAT IS LIT, AND HOW A GENERIC MACHINE CAN BE ASKED
// ---------------------------------------------------
// A lamp has an id, a colour and a **group**, and no human name — so "what is lit" cannot be a list
// of lamp names, because there are none. The group is the author's own grouping and is the closest
// thing to a name the format guarantees, so the report is by group with a count: *"three of the four
// nova lanes"*. That is also what a sighted player actually sees — nobody reads individual inserts,
// they read the shape a group makes.
//
// **The next objective is the author's declaration order**, which is the same argument
// `RuleEngine.runningMode` makes for picking the mode it names: the order a rulesheet declares its
// modes in is a deliberate statement about progression, and any other ordering here would be this
// file inventing a difficulty model for somebody else's machine. So the objective is the first
// declared mode that is not already running — and when they are all running, the honest answer is
// the one every pinball machine has: keep the ball alive.
//
// Pure. It takes a snapshot and returns a sentence, so a test drives every branch without a
// simulation and the two clients can speak the same words.

/** One lamp group, and how much of it is lit. */
export interface LitGroup {
  readonly group: string
  readonly lit: number
  readonly total: number
}

/** Everything the report is built from. A snapshot, taken by the caller, of one instant. */
export interface AloudSnapshot {
  readonly ball: number
  readonly ballsPerGame: number
  readonly score: number
  /** Which seat is up, and how many there are. One player is the common case and says nothing. */
  readonly player: number
  readonly players: number
  /** `attract`, `ballInPlay`, `gameOver` … whatever the game calls it. */
  readonly phase: string
  readonly tilted: boolean
  /** The mode running now, if any, with what is left of its clock in whole seconds. */
  readonly mode: { readonly title: string; readonly secondsLeft: number } | undefined
  /** Lit groups, in the machine's own declaration order. Groups with nothing lit are omitted. */
  readonly lit: readonly LitGroup[]
  /** The next thing to go for, already chosen by the caller from the rulesheet's own order. */
  readonly objective: string | undefined
  /** Balls on the playfield. Two or more is a multiball and is worth saying. */
  readonly ballsInPlay: number
}

/**
 * The whole report, as one string.
 *
 * One string rather than a list of lines, because both consumers speak it: the flat client hands it
 * to `speechSynthesis` and the headset to its own voice, and a list would make each of them decide
 * on the joining. Sentences, so a synthesizer pauses in the right places.
 */
export function readMachineAloud(snapshot: AloudSnapshot): string {
  return sentences(snapshot).join(' ')
}

/**
 * The report as its sentences, which is what a caption panel wants.
 *
 * NFR-022 asks for every spoken line to appear as text as well, and a caption panel is 42 glyphs
 * wide on the headset — so it gets the sentences and wraps them, rather than being handed one
 * paragraph and having to find the breaks itself.
 */
export function aloudSentences(snapshot: AloudSnapshot): readonly string[] {
  return sentences(snapshot)
}

function sentences(snapshot: AloudSnapshot): string[] {
  const said: string[] = []

  // **The state of the game first, because it changes what everything else means.** A score read
  // out during attract is last game's, and a report that opened with it would be answering a
  // question nobody asked.
  if (snapshot.phase === 'attract') {
    said.push('The machine is in attract mode. Press start for a new game.')
    return said
  }

  said.push(`Ball ${snapshot.ball} of ${snapshot.ballsPerGame}.`)
  if (snapshot.players > 1) said.push(`Player ${snapshot.player} of ${snapshot.players}.`)
  said.push(`Score ${spokenNumber(snapshot.score)}.`)

  if (snapshot.tilted) {
    // **Said early and said plainly.** A tilted machine ignores the flippers, and a player who
    // cannot see the TILT on the display is otherwise left to work that out by pressing them.
    said.push('Tilted. This ball is over; the flippers are dead.')
  }

  if (snapshot.ballsInPlay > 1) said.push(`${countWord(snapshot.ballsInPlay)} balls in play.`)

  if (snapshot.mode !== undefined) {
    said.push(
      snapshot.mode.secondsLeft > 0
        ? `${snapshot.mode.title} is running, ${snapshot.mode.secondsLeft} seconds left.`
        : `${snapshot.mode.title} is running.`,
    )
  }

  const lit = snapshot.lit.filter((group) => group.lit > 0)
  if (lit.length === 0) said.push('Nothing is lit.')
  else said.push(`Lit: ${lit.map(litPhrase).join(', ')}.`)

  if (snapshot.phase === 'gameOver') said.push('Game over.')
  else if (snapshot.objective !== undefined) said.push(`Next: ${snapshot.objective}.`)

  return said
}

/** "three of the four nova lanes", or "all six star systems". */
function litPhrase(group: LitGroup): string {
  const name = words(group.group)
  if (group.lit >= group.total) return `all ${countWord(group.total)} ${name}`
  return `${countWord(group.lit)} of ${countWord(group.total)} ${name}`
}

/**
 * A camelCase identifier as words a synthesizer will not spell out.
 *
 * `novaLanes` becomes "nova lanes". Lower-cased rather than title-cased: this lands in the middle
 * of a sentence, and a synthesizer reading "Nova Lanes" mid-clause puts a stress on it that makes
 * the list sound like a series of headings.
 */
export function words(identifier: string): string {
  return identifier
    .replace(/([a-z0-9])([A-Z])/gu, '$1 $2')
    .replace(/[-_]+/gu, ' ')
    .toLowerCase()
    .trim()
}

/**
 * Small numbers as words, larger ones as digits.
 *
 * A synthesizer says "4" and "four" identically, so this is for the *caption*: "3 of 4 nova lanes"
 * reads as a score and "three of four nova lanes" reads as a sentence. Past ten the word is longer
 * than the number and stops helping.
 */
export function countWord(n: number): string {
  const words = [
    'zero', 'one', 'two', 'three', 'four', 'five',
    'six', 'seven', 'eight', 'nine', 'ten',
  ]
  return n >= 0 && n < words.length ? words[n]! : String(n)
}

/**
 * A score, grouped so it is heard rather than spelled.
 *
 * **`1,250,000` and not `1250000`.** A synthesizer handed the bare digits of a pinball score reads
 * "one million two hundred and fifty thousand", which is right — and handed a seven-digit string
 * with no separators, several of them read it digit by digit instead. The separator is what makes
 * the difference, and it is also what a caption needs.
 */
export function spokenNumber(value: number): string {
  return Math.round(value).toLocaleString('en-GB')
}

/**
 * The next thing to go for, from the rulesheet's own declaration order.
 *
 * **The author's order, deliberately.** It is the same argument `RuleEngine.runningMode` makes for
 * the mode it names: the order a rulesheet declares its modes in is a statement about progression,
 * and any other ordering would be this module inventing a difficulty model for somebody else's
 * machine. A mode already running is not an objective — it is the current one — so it is skipped.
 *
 * `undefined` when every declared mode is already running, and the caller says the thing every
 * pinball machine leaves you with instead.
 */
export function nextObjective(
  modes: readonly { readonly id: string; readonly title: string }[],
  active: readonly string[],
): string | undefined {
  for (const mode of modes) {
    if (!active.includes(mode.id)) return mode.title
  }
  return undefined
}
